import { useMemo, useState } from 'react';
import { StatusGlyph } from '../board/StatusGlyph';
import { NookThumb, TintedNookGlyph } from '../workspaces/NookCanvas';
import { shortWhen } from '../design/dates';
import { useCatalog } from '../entity/catalogStore';
import { usePlan } from '../plan/usePlan';
import { useVault } from '../session/useVault';
import { buildCorpus, itemFor } from '../thoughts/corpus';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { nameTaken } from '../nook/newArc';
import { NewHolderDraft } from './NewHolderDraft';
import { PickDialog } from './PickDialog';
import { useHolderWrites } from './useHolderWrites';
import type { PickGroup } from './PickDialog';
import { addCandidates, createOffers, holderCandidates } from './membership';
import type { HolderCandidate, HolderKind, Holder, Item } from './membership';

const NO_ARCS: never[] = [];

/**
 * From a holder's page: search what can be put into it, and take one. Arcs go
 * into its `arcs:`, a recording or note into its sources.
 */
export function AddIntoDialog({
  holder,
  onAdd,
  onClose,
}: {
  holder: Holder;
  onAdd: (items: Item[]) => void;
  onClose: () => void;
}) {
  const { plan } = usePlan();
  const { state } = useVault();
  const catalog = useCatalog();
  const arcs = plan.status === 'ready' ? plan.plan.arcs : NO_ARCS;
  const corpus = useMemo(() => (state.status === 'ready' ? buildCorpus(state.vault) : []), [state]);

  const groups = (query: string): PickGroup[] => {
    const found = addCandidates({
      holder,
      arcs,
      corpus,
      nameFor: (path) => catalog.get(path)?.name ?? null,
      query,
    });
    const rowsOf = (kind: Item['kind']) =>
      found
        .filter((c) => c.item.kind === kind)
        .map((c) => {
          const at = kind === 'arc' ? null : itemFor(c.item.path).at;
          const arc = kind === 'arc' ? arcs.find((a) => a.path === c.item.path) : undefined;
          return {
            key: c.key,
            label: c.item.label,
            meta: at ? shortWhen(at) : undefined,
            glyph: arc ? <StatusGlyph status={arc.status} /> : undefined,
            run: () => onAdd([c.item]),
          };
        });
    return [
      { name: 'Arcs', rows: rowsOf('arc') },
      { name: 'Recordings', rows: rowsOf('recording') },
      { name: 'Notes', rows: rowsOf('note') },
    ];
  };

  return (
    <PickDialog
      label="Add"
      placeholder="Add an arc, a recording or a note"
      groups={groups}
      hint="↵ add · esc close"
      onClose={onClose}
    />
  );
}

/**
 * From a selection: search where it can go, arcs and live nooks, and take one.
 * What was typed can become a new arc or nook when it names none.
 */
export function AddToDialog({
  items,
  onPick,
  onCreate,
  onClose,
}: {
  items: Item[];
  onPick: (holder: HolderCandidate) => void;
  onCreate: (kind: HolderKind, name: string) => void;
  onClose: () => void;
}) {
  const { plan } = usePlan();
  const catalog = useCatalog();
  const nooks = useWorkspaces();
  const arcs = plan.status === 'ready' ? plan.plan.arcs : NO_ARCS;
  const live = nooks.status === 'ready' ? nooks.workspaces : [];

  const groups = (query: string): PickGroup[] => {
    const found = holderCandidates({ items, arcs, nooks: live, catalog, query });
    const row = (h: HolderCandidate) => ({
      key: h.key,
      label: h.label,
      labelNode: h.nook && h.label === '' ? <NookThumb nook={h.nook} /> : undefined,
      meta: h.kind === 'nook' ? 'nook' : undefined,
      glyph: h.arc ? (
        <StatusGlyph status={h.arc.status} />
      ) : h.kind === 'nook' ? (
        <TintedNookGlyph tags={h.nook?.tags} />
      ) : undefined,
      run: () => onPick(h),
    });
    const name = query.trim();
    return [
      { name: 'Arcs', rows: found.filter((h) => h.kind === 'arc').map(row) },
      { name: 'Nooks', rows: found.filter((h) => h.kind === 'nook').map(row) },
      {
        name: 'New',
        rows: createOffers(query, found).map((kind) => ({
          key: `new-${kind}`,
          label: `New ${kind} ‹${name}›`,
          run: () => onCreate(kind, name),
          keepOpen: true,
        })),
      },
    ];
  };

  return (
    <PickDialog
      label="Add to"
      placeholder={items.length === 1 ? 'Add to an arc or a nook' : `Add ${items.length} to an arc or a nook`}
      groups={groups}
      hint="↵ add · esc close"
      onClose={onClose}
    />
  );
}

/**
 * Add to…, end to end: pick where [items] go, or start a new arc or nook from
 * what was typed — which opens a draft to name and confirm first. [onDone]
 * fires once something has been written.
 */
export function AddToFlow({
  items,
  onDone,
  onClose,
}: {
  items: Item[];
  onDone: () => void;
  onClose: () => void;
}) {
  const { plan } = usePlan();
  const { addItems, createHolding } = useHolderWrites();
  const [drafting, setDrafting] = useState<{ kind: HolderKind; name: string } | null>(null);
  const arcs = plan.status === 'ready' ? plan.plan.arcs : NO_ARCS;

  if (drafting) {
    return (
      <NewHolderDraft
        kind={drafting.kind}
        initialName={drafting.name}
        items={items}
        nameTaken={(name) => nameTaken(name, arcs)}
        onCancel={() => setDrafting(null)}
        onCreate={(draft) => {
          void createHolding(drafting.kind, draft.name, draft.items, draft.status);
          onDone();
          onClose();
        }}
      />
    );
  }

  return (
    <AddToDialog
      items={items}
      onPick={(holder) => {
        void addItems(holder, items);
        onDone();
      }}
      onCreate={(kind, name) => setDrafting({ kind, name })}
      onClose={onClose}
    />
  );
}
